






























import { computed, defineComponent, onBeforeUnmount, PropType, ref } from "@vue/composition-api";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { SubscriptionLevel } from "@scrile/api-provider/dist/api/SubscriptionLevelProvider";
import { ContentFeed } from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import { useMaxCurrentSubscription } from "@/hooks/useMaxCurrentSubscription";
import providers from "@/lib/api";
import emitter from "@/lib/emitter";
import { getCost } from "@/lib/moneyHelpers";
import { EVENTS, SUBSCRIPTION_LEVEL_FRAGMENT } from "@/const";
import BaseIcon from "@/components/Base/BaseIcon";
import BaseButton from "@/components/BaseButton";
import FormatMoney from "@/components/FormatMoney";

export default defineComponent({
  name: "ContentFeedUnlockFeed",
  components: { BaseIcon, FormatMoney, BaseButton },
  props: {
    contentFeed: { type: Object as PropType<ContentFeed>, required: true },
    paymentPackages: { type: Array as PropType<PaymentPackage[]> },
  },
  setup(props) {
    const { amount: activeSubscriptionAmount, getMaxCurrentSubscription } = useMaxCurrentSubscription(
      props.contentFeed.user.id
    );
    const loading = ref(false);
    const subscriptionLevelHasFiles = ref(false);
    const subscriptionLevelsWithContent = ref<SubscriptionLevel[]>([]);
    const getPostOwnerSubscriptionLevels = async (): Promise<void> => {
      let result: SubscriptionLevel[];
      result = await providers.SubscriptionLevelProvider.find(
        { userIds: [props.contentFeed.user.id] },
        SUBSCRIPTION_LEVEL_FRAGMENT
      );
      if (result) {
        subscriptionLevelsWithContent.value = result.filter(i => i?.contentCount ?? 0 > 0);
        subscriptionLevelHasFiles.value = subscriptionLevelsWithContent.value.length > 0;
      }
    };

    const maxSubscriptionAmount = computed(() => {
      return Math.max(0, ...subscriptionLevelsWithContent.value.map(i => i.pricePackage?.amount ?? 0));
    });

    const postSubscriptionAmount = computed(() => {
      return (
        subscriptionLevelsWithContent.value.find(i => i.id === props.contentFeed.subscriptionLevelId)?.pricePackage
          ?.amount ?? 0
      );
    });
    const canAccessPostViaSubscription = computed(() => {
      return (
        subscriptionLevelHasFiles.value &&
        activeSubscriptionAmount.value < maxSubscriptionAmount.value &&
        activeSubscriptionAmount.value < postSubscriptionAmount.value
      );
    });

    const showUnlockButton = computed(() => {
      return props.contentFeed.pricePackageId !== "0" && !props.contentFeed.purchased;
    });

    const postPaymentPackage = computed(() => {
      if (!props.paymentPackages) return null;
      return props.paymentPackages.find(p => p.id === props.contentFeed.pricePackageId) ?? null;
    });

    const oneTimeCost = computed(() => {
      if (props.contentFeed.pricePackageId === "0" || !postPaymentPackage.value) return 0;
      return getCost(postPaymentPackage.value);
    });

    const getData = async () => {
      try {
        loading.value = true;
        await Promise.all([getPostOwnerSubscriptionLevels(), getMaxCurrentSubscription()]);
      } finally {
        loading.value = false;
      }
    };

    const onPurchasePost = async ({ userId }: { userId: string }): Promise<void> => {
      if (userId === props.contentFeed.user.id) {
        await getMaxCurrentSubscription();
      }
    };

    getData();

    emitter.on<any>(EVENTS.CONTENT_FEED_PURCHASED, onPurchasePost);
    onBeforeUnmount(() => {
      emitter.off<any>(EVENTS.CONTENT_FEED_PURCHASED, onPurchasePost);
    });

    return { loading, canAccessPostViaSubscription, postSubscriptionAmount, oneTimeCost, showUnlockButton };
  },
});
